import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import {
  federalList,
  newSalutation,
  phoneConstraints,
  phoneValidationMessages,
} from './ticket-created-shared.field-configs';
import { checkIfEmailIdentical } from '@shared/utils/email-validator';
import {
  RegexpEmail,
  RegexpLocationname,
  RegexpStreetname,
  RegexpZipCodeDE,
} from '@shared/utils/regex';

export function requestingContact(
  translateService: TranslateService
): Array<FormlyFieldConfig> {
  return [
    {
      expressions: {
        hide: (field: any) => {
          // Verfügungsberechtigter oder Andere
          return (
            (field.model?.concerningLocation?.powerOfDisposition ===
              'Eigentümer' ||
              !field.model?.concerningLocation?.powerOfDisposition) &&
            field.model?.auxAddress
          );
        },
      },
      wrappers: [],
      fieldGroup: [
        {
          type: 'chapter',
          props: {
            chapterTitle: translateService.instant(
              'ticket.create.headline__contact'
            ),
            chapterText: translateService.instant(
              'ticket.create.p__info_contact'
            ),
          },
          expressionProperties: {
            'props.visibleInConfirm':
              'field.model?.concerningLocation?.powerOfDisposition!=="Eigentümer"',
          },
        },
        {
          key: 'requestingContact',
          fieldGroup: [
            ...newSalutation(translateService),
            {
              type: 'flex-layout',

              fieldGroup: [
                {
                  type: 'input',
                  key: 'firstname',
                  props: {
                    label: translateService.instant(
                      'ticket.create.label__firstName'
                    ),
                    visibleInConfirm: true,
                    required: true,
                    maxLength: 50,
                  },
                },
                {
                  type: 'input',
                  key: 'lastname',
                  props: {
                    label: translateService.instant(
                      'ticket.create.label__lastName'
                    ),
                    visibleInConfirm: true,
                    maxLength: 50,
                    required: true,
                  },
                },
              ],
            },
            {
              type: 'input',
              key: 'accountString',
              props: {
                label: translateService.instant('ticket.create.label__company'),
                visibleInConfirm: true,
                maxLength: 50,
                required: false,
              },
            },
            {
              type: 'flex-layout',

              fieldGroup: [
                {
                  type: 'input',
                  key: 'phonenumber',
                  defaultValue: '0049',
                  wrappers: ['tooltip', 'form-field'],
                  props: {
                    label: translateService.instant(
                      'ticket.create.label__phone'
                    ),
                    visibleInConfirm: true,
                    required: true,
                    tooltip: translateService.instant(
                      'ticket.create.tooltip__phone'
                    ),
                    ...phoneConstraints,
                  },
                  validation: phoneValidationMessages(translateService),
                },
              ],
            },
            {
              type: 'flex-layout',

              validators: {
                fieldMatch: {
                  expression: checkIfEmailIdentical,
                  message: translateService.instant(
                    'formErrors.email.not_identical'
                  ),
                  errorPath: 'confirmEmail',
                },
              },
              fieldGroup: [
                {
                  type: 'input',
                  key: 'email',
                  props: {
                    label: translateService.instant(
                      'ticket.create.label__email'
                    ),
                    visibleInConfirm: true,
                    required: true,
                    pattern: RegexpEmail,
                    maxLength: 100,
                  },
                  validation: {
                    messages: {
                      pattern: () =>
                        translateService.instant('formErrors.email.pattern'),
                    },
                  },
                },
                {
                  type: 'input',
                  key: 'confirmEmail',
                  props: {
                    label: translateService.instant(
                      'ticket.create.label__confirm_email'
                    ),
                    required: true,
                  },
                },
              ],
            },
            {
              key: 'address',
              fieldGroup: [
                {
                  type: 'flex-layout',

                  fieldGroup: [
                    {
                      type: 'input',
                      key: 'street',
                      props: {
                        required: true,
                        label: translateService.instant(
                          'ticket.create.label__street_contact'
                        ),
                        visibleInConfirm: true,
                        maxLength: 250,
                        minLength: 3,
                        pattern: RegexpStreetname,
                      },
                      validation: {
                        messages: {
                          pattern: translateService.instant(
                            'formErrors.street.pattern'
                          ),
                          minlength: err =>
                            translateService.instant(
                              'formErrors.street.minLength',
                              {
                                length: err.requiredLength,
                              }
                            ),
                        },
                      },
                      expressionProperties: {
                        'props.visibleInConfirm':
                          'field.model?.powerOfDisposition!=="Eigentümer"',
                      },
                    },
                    {
                      type: 'input',
                      key: 'zipcode',
                      props: {
                        label: translateService.instant(
                          'ticket.create.label__zipcode_contact'
                        ),
                        required: true,
                        visibleInConfirm: true,
                        minLength: 5,
                        maxLength: 20,
                        pattern: RegexpZipCodeDE,
                      },
                      validation: {
                        messages: {
                          pattern: translateService.instant(
                            'formErrors.zipcode.pattern'
                          ),
                        },
                      },
                      expressionProperties: {
                        'props.visibleInConfirm':
                          'field.model?.powerOfDisposition!=="Eigentümer"',
                      },
                    },
                  ],
                },
                {
                  type: 'flex-layout',

                  fieldGroup: [
                    {
                      type: 'input',
                      key: 'city',
                      props: {
                        label: translateService.instant(
                          'ticket.create.label__city_contact'
                        ),
                        required: true,
                        visibleInConfirm: true,
                        minLength: 3,
                        maxLength: 80,
                        pattern: RegexpLocationname,
                      },
                      validation: {
                        messages: {
                          pattern: translateService.instant(
                            'formErrors.city.pattern'
                          ),
                          minlength: err =>
                            translateService.instant(
                              'formErrors.city.minLength',
                              {
                                length: err.requiredLength,
                              }
                            ),
                        },
                      },
                      expressionProperties: {
                        'props.visibleInConfirm':
                          'field.model?.powerOfDisposition!=="Eigentümer"',
                      },
                    },
                    {
                      type: 'select',
                      key: 'state',
                      props: {
                        label: translateService.instant(
                          'ticket.create.label__state_contact'
                        ),
                        required: true,
                        visibleInConfirm: true,
                        options: federalList(translateService.currentLang),
                      },
                      expressionProperties: {
                        'props.visibleInConfirm':
                          'field.model?.powerOfDisposition!=="Eigentümer"',
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ];
}
